/**
 * Audit Log Service (Frontend)
 *
 * HTTP client for the audit log API. Read-only — supports
 * paginated queries and CSV export downloads.
 */
import { getBackendUrl, isDevMode } from './configService';

const getApiBaseUrl = (): string => {
  const url = getBackendUrl();
  if (!url) {
    throw new Error('BACKEND_URL not configured in runtime config');
  }
  return url;
};

function getAuthHeaders(): HeadersInit {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('access_token');
  if (token && token !== 'dev-mode-token') {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export interface AuditLogEntry {
  id: string;
  user_id: string;
  username: string;
  roles: string[];
  action: string;
  feature: string;
  entity_id: string | null;
  endpoint: string;
  method: string;
  ip_address: string | null;
  payload: Record<string, unknown> | null;
  created_at: string;
}

export interface AuditFetchParams {
  page?: number;
  limit?: number;
  search?: string;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  user?: string;
  feature?: string;
  action?: string;
  role?: string;
  method?: string;
  endpoint?: string;
  entityId?: string;
  startDate?: string;
  endDate?: string;
}

export interface AuditFetchResult {
  data: AuditLogEntry[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

/**
 * Fetch paginated audit log entries.
 */
export async function fetchAuditLogs(params: AuditFetchParams): Promise<AuditFetchResult> {
  const qs = new URLSearchParams();
  if (params.page !== undefined) qs.set('page', String(params.page));
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  if (params.search) qs.set('search', params.search);
  if (params.sortField) qs.set('sortField', params.sortField);
  if (params.sortDirection) qs.set('sortDirection', params.sortDirection);
  if (params.user) qs.set('user', params.user);
  if (params.feature) qs.set('feature', params.feature);
  if (params.action) qs.set('action', params.action);
  if (params.role) qs.set('role', params.role);
  if (params.method) qs.set('method', params.method);
  if (params.endpoint) qs.set('endpoint', params.endpoint);
  if (params.entityId) qs.set('entityId', params.entityId);
  if (params.startDate) qs.set('startDate', params.startDate);
  if (params.endDate) qs.set('endDate', params.endDate);

  const url = `${getApiBaseUrl()}/api/audit-log?${qs.toString()}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Audit log fetch failed: HTTP ${response.status}`);
  }

  const json = await response.json();
  if (!json.success) {
    throw new Error(json.message || 'Audit log fetch failed');
  }

  return {
    data: json.data,
    total: json.total,
    page: json.page,
    limit: json.limit,
    hasMore: json.hasMore,
  };
}

/**
 * Trigger CSV export download for audit log entries.
 */
export async function exportAuditLogs(params: AuditFetchParams): Promise<void> {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.sortField) qs.set('sortField', params.sortField);
  if (params.sortDirection) qs.set('sortDirection', params.sortDirection);
  if (params.user) qs.set('user', params.user);
  if (params.feature) qs.set('feature', params.feature);
  if (params.action) qs.set('action', params.action);
  if (params.role) qs.set('role', params.role);
  if (params.method) qs.set('method', params.method);
  if (params.endpoint) qs.set('endpoint', params.endpoint);
  if (params.entityId) qs.set('entityId', params.entityId);
  if (params.startDate) qs.set('startDate', params.startDate);
  if (params.endDate) qs.set('endDate', params.endDate);

  const url = `${getApiBaseUrl()}/api/audit-log/export?${qs.toString()}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Audit log export failed: HTTP ${response.status}`);
  }

  const blob = await response.blob();
  const downloadUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = 'audit_log_export.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(downloadUrl);
}
